import { appendDebugEntry, clearDebugEntriesForGroup, createDebugEntry, type DebugEntry, type DebugGroup } from './session-debug-model';
import { bindSessionDebugPanel } from './session-debug-panel';

function readBooleanDebugFlag(key: string, fallback = false): boolean {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return fallback;
    return value === '1' || value === 'true' || value === 'yes';
  } catch {
    return fallback;
  }
}

const SESSION_DEBUG_ENABLED = readBooleanDebugFlag('webcoding-debug-session-reply', true);
const COMPOSER_FOCUS_DEBUG_ENABLED = false;
const SESSION_FLOW_DEBUG_ENABLED = true;
const SCROLL_DEBUG_ENABLED = true;
const MAX_DEBUG_LINES = 80;

const entries: DebugEntry[] = [];
let panelController: { refresh: () => void } | null = null;
const lastScrollLogAt = new Map<string, number>();

function ensurePanelController(): void {
  if (panelController || !SESSION_DEBUG_ENABLED || typeof document === 'undefined') return;
  const bind = () => {
    if (panelController) return;
    panelController = bindSessionDebugPanel({
      entries,
      onClearGroup: (group: DebugGroup) => {
        clearDebugEntriesForGroup(entries, group);
        panelController?.refresh();
      },
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
}

function appendDebugLog(group: DebugGroup, event: string, payload: unknown = {}): void {
  const entry = createDebugEntry(group, event, payload);
  console.info(entry.text);
  appendDebugEntry(entries, entry, MAX_DEBUG_LINES);
  ensurePanelController();
  panelController?.refresh();
}

ensurePanelController();

export function logSessionDebug(event: string, payload: unknown = {}): void {
  void event;
  void payload;
}

export function logSidebarSessionDebug(event: string, payload: unknown = {}): void {
  void event;
  void payload;
}

export function logAssistantCopyDebug(event: string, payload: unknown = {}): void {
  void event;
  void payload;
}

export function logAssistantRebuildDebug(event: string, payload: unknown = {}): void {
  void event;
  void payload;
}

export function logSessionLoadDebug(event: string, payload: unknown = {}): void {
  if (!SESSION_DEBUG_ENABLED) return;
  appendDebugLog('connection-efficiency', event, payload);
}

export function clearSessionDebugLog(): void {
  entries.length = 0;
  console.clear();
  panelController?.refresh();
}

export function logComposerFocusDebug(event: string, payload: unknown = {}): void {
  if (!COMPOSER_FOCUS_DEBUG_ENABLED) return;
  void event;
  void payload;
}

export function logSessionFlowDebug(event: string, payload: unknown = {}): void {
  if (!SESSION_FLOW_DEBUG_ENABLED) return;
  if (!SESSION_DEBUG_ENABLED) return;
  appendDebugLog('connection-efficiency', event, payload);
}

export function logCodexBubbleDebug(event: string, payload: unknown = {}): void {
  logCodexSteeringDebug(`bubble:${event}`, payload);
}

export function logCodexSteeringDebug(event: string, payload: unknown = {}): void {
  if (!SESSION_DEBUG_ENABLED) return;
  appendDebugLog('codex-steering', event, payload);
}

export function logScrollDebug(event: string, payload: unknown = {}): void {
  if (!SCROLL_DEBUG_ENABLED) return;
  if (event.startsWith('capture-scroll-snapshot')) {
    // 连续滚动只保留采样点，加载和连接事件仍完整记录。
    const now = Date.now();
    const previous = lastScrollLogAt.get(event) || 0;
    if (now - previous < 200) return;
    lastScrollLogAt.set(event, now);
  }
  if (!SESSION_DEBUG_ENABLED) return;
  appendDebugLog('connection-efficiency', event, payload);
}

export function logConnectionDebug(event: string, payload: unknown = {}): void {
  if (!SESSION_DEBUG_ENABLED) return;
  appendDebugLog('connection-efficiency', `ws:${event}`, payload);
}
